import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';

import { scheduleConflictOf, type ScheduleAction } from '../api/schedule-errors';
import { useScheduleName } from '../hooks/use-schedule';
import { useScheduleErrorMessage } from '../hooks/use-schedule-error-message';

export interface ScheduleErrorAlertProps {
  error: unknown;
  action: ScheduleAction;
  /** Titles of the products the call sent, to name the ones a conflict lists. */
  titles?: ReadonlyMap<string, string>;
}

/**
 * A failed schedule call where the screen shows it: the message for its code, the schedule that
 * already owns the products on a conflict (named and linked), and the request id for support.
 */
export function ScheduleErrorAlert({ error, action, titles }: ScheduleErrorAlertProps) {
  const { t } = useTranslation(['schedules', 'errors']);
  const message = useScheduleErrorMessage();
  const conflict = scheduleConflictOf(error);
  const owner = useScheduleName(conflict?.scheduleId ?? null);
  const requestId = isApiError(error) ? error.requestId : undefined;
  const named =
    conflict && titles
      ? conflict.productIds.flatMap((id) => {
          const title = titles.get(id);
          return title ? [title] : [];
        })
      : [];

  return (
    <Alert variant="destructive" role="alert">
      {conflict ? <AlertTitle>{t('schedules:errors.conflictTitle')}</AlertTitle> : null}
      <AlertDescription>
        <p>{message(error, action)}</p>
        {conflict?.scheduleId ? (
          <p>
            {t('schedules:errors.ownedBy')}{' '}
            <Link
              to="/org/schedules/$scheduleId"
              params={{ scheduleId: conflict.scheduleId }}
              className="font-medium underline underline-offset-2"
            >
              {owner.data ?? t('schedules:errors.otherSchedule')}
            </Link>
          </p>
        ) : null}
        {named.length > 0 ? (
          <p className="text-xs">
            {t('schedules:errors.conflictProducts', { list: named.join(', ') })}
          </p>
        ) : null}
        {conflict ? <p className="text-xs">{t('schedules:errors.conflictHint')}</p> : null}
        {requestId ? (
          <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
